<template>
    <div>
        <p
            v-if="errorMessage"
            class="flex justify-center items-center text-red-600 font-semibold my-2"
        >
            <XCircleIcon class="h-5 w-5 mr-2 text-red-500" aria-hidden="true" />
            {{ errorMessage }}
        </p>
        <p
            v-if="added"
            class="flex justify-center items-center text-nutreance-green font-semibold my-2"
        >
            <CheckCircleIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            Added!
        </p>

        <button
            @click="onClick"
            type="button"
            :class="[
                added
                    ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                    : 'bg-nutreance-green hover:bg-nutreance-green-darker text-white border-transparent',
                'mx-auto flex justify-center items-center px-6 py-3 border rounded-md shadow-sm font-medium focus:outline-none',
            ]"
        >
            <span v-if="added"> Continue to Cart </span>
            <ArrowSmRightIcon
                v-if="added"
                class="h-6 w-6 ml-2"
                aria-hidden="true"
            />

            <LoadingIcon
                v-if="loading && !added"
                class="h-5 w-5 mr-2 text-white"
            />
            <ShoppingCartIcon
                v-if="!loading && !added"
                class="h-5 w-5 mr-2"
                aria-hidden="true"
            />
            <span v-if="!added">
                Add to Cart - ${{
                    dollarsForDisplay(product.default_product_option.price)
                }}
            </span>
        </button>
    </div>
</template>

<script setup lang="ts">
import { inject, ref } from "vue";
import {
    ArrowSmRightIcon,
    CheckCircleIcon,
    ShoppingCartIcon,
    XCircleIcon,
} from "@heroicons/vue/solid";
import { useMoney } from "../../functions/useMoney";
import LoadingIcon from "../LoadingIcon.vue";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import { addCartItem } from "../../api/cart";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    product?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;
const { dollarsForDisplay } = useMoney();
const { pushAddToCartToDataLayer } = useGoogleDataLayer();

const loading = ref(false);
const added = ref(false);
const errorMessage = ref<string | null>(null);

function onClick() {
    if (added.value) {
        emitter.emit("openCartSideBar");
    } else {
        addToCart();
    }
}

function addToCart() {
    if (loading.value) {
        return;
    }

    loading.value = true;

    addCartItem({
        product_option_id: props.product.default_product_option.id,
        quantity: 1,
    })
        .then((data) => {
            errorMessage.value = null;
            added.value = true;
            emitter.emit("cartItemUpdated", data);

            pushAddToCartToDataLayer({
                product: props.product,
                subscribe: false,
                quantity: 1,
                source: "Health Profile Results",
            });
        })
        .catch((error: any) => {
            if (
                typeof error.response !== "undefined" &&
                Object.prototype.hasOwnProperty.call(error.response, "data") &&
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                errorMessage.value = error.response.data.errors[field][0];
            }
        })
        .finally(() => (loading.value = false));
}
</script>
